import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Sparkle, ArrowLeft, EnvelopeSimple, CheckCircle } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api } from "../lib/api";
import GlobalHeader from "../components/layout/GlobalHeader";

/**
 * Sprint H · Batch 6 — Pantalla "He olvidado mi contraseña".
 *
 * Igual para los 5 roles (Super-Admin, Titular, Gestor, Colaborador, Empleado)
 * y para Cliente final. El backend detecta automáticamente el tipo de cuenta
 * por email.
 *
 * Anti-enumeration: la respuesta siempre es "te hemos enviado un email si la
 * cuenta existe" — nunca confirmamos si el email está registrado o no.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  // El path /cliente/recuperar-password indica que viene del flujo cliente.
  const fromCustomer = location.pathname.includes("/cliente/");
  const backTo = fromCustomer ? "/cliente/login" : "/login";

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr("");
    const value = (email || "").trim().toLowerCase();
    if (!value || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) {
      setErr("Introduce un email válido.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: value });
      setSent(true);
    } catch (e) {
      // El backend siempre responde 200 — esto solo cubre fallos de red
      setErr("No se ha podido procesar la solicitud. Reintenta en unos segundos.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen ag-light-surface flex flex-col" data-testid="forgot-password-page">
      <GlobalHeader variant="auto" testId="forgot-header" showRegisterCta={false} />
      <div className="flex-1 flex items-start sm:items-center justify-center px-4 py-6 sm:py-10">
      <div className="w-full max-w-md bg-white border border-gray-200 rounded-md shadow-sm p-6 sm:p-8">
        <div className="flex items-center gap-2 mb-6">
          <Sparkle size={24} weight="duotone" className="text-[var(--ag-gold)]" />
          <span className="font-display font-black tracking-tighter text-xl text-[#1A1A1A]">AutoGlow</span>
        </div>

        {!sent ? (
          <>
            <h1 className="text-xl font-bold mb-1 text-[#1A1A1A]">¿Olvidaste tu contraseña?</h1>
            <p className="text-sm text-gray-600 mb-6">
              Introduce el email asociado a tu cuenta y te enviaremos un enlace
              para crear una nueva. El enlace caduca en 1 hora.
            </p>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="forgot-email" className="text-xs uppercase tracking-wider">Email</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  autoComplete="email"
                  placeholder="tu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="rounded-sm mt-1"
                  data-testid="forgot-email-input"
                  required
                />
              </div>
              {err && <div className="text-xs text-red-600" data-testid="forgot-error">{err}</div>}
              <Button
                type="submit"
                disabled={busy}
                className="w-full bg-[var(--ag-gold)] hover:bg-[#FFCD3C] rounded-sm h-11"
                data-testid="forgot-submit"
              >
                <EnvelopeSimple size={16} weight="fill" className="mr-1.5" />
                {busy ? "Enviando…" : "Enviar enlace"}
              </Button>
            </form>
          </>
        ) : (
          <div data-testid="forgot-success">
            <div className="mb-4 flex items-center gap-2 text-emerald-600">
              <CheckCircle size={28} weight="fill" />
              <span className="font-display font-bold text-lg">Comprueba tu email</span>
            </div>
            <p className="text-sm text-gray-600">
              Si <b>{email}</b> está registrado, en menos de un minuto recibirás
              un enlace para restablecer tu contraseña. El enlace solo es válido
              durante <b>1 hora</b> y solo puede usarse una vez.
            </p>
            <p className="text-xs text-gray-500 mt-4">
              ¿No te llega? Revisa la bandeja de spam o solicita uno nuevo
              tras 5 minutos.
            </p>
          </div>
        )}

        <div className="mt-6 pt-4 border-t border-gray-100">
          <Link to={backTo} className="inline-flex items-center gap-1 text-xs text-[var(--ag-gold)] hover:underline" data-testid="forgot-back-login">
            <ArrowLeft size={12} weight="bold" />
            Volver al inicio de sesión
          </Link>
        </div>
      </div>
      </div>
    </div>
  );
}
